import { Check, Copy, Sparkles } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Tooltip as T } from 'radix-ui';
import { absolute, ago, freshness, shortAgo } from '../lib/time';
import { cn } from './cn';

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={cn('inline-flex h-5 min-w-5 items-center justify-center rounded border border-line bg-panel px-1 font-sans text-[11px] font-medium text-ink-3', className)}>{children}</kbd>;
}

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const modKey = isMac ? '⌘' : 'Ctrl';

export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cn('block skeleton h-3', className)} />;
}

/** Re-render periodically so relative times stay true while a page is open. */
export function useNow(intervalMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), intervalMs); return () => clearInterval(id); }, [intervalMs]);
  return now;
}

export function TimeAgo({ value, now, compact = false, never = 'Never', className }: {
  value: string | null; now?: number; compact?: boolean; never?: string; className?: string;
}) {
  const current = useNow();
  const at = now ?? current;
  if (!value) return <span className={cn('text-ink-3 italic', className)} title="No check-in has been recorded">{never}</span>;
  const f = freshness(value, at);
  return (
    <time dateTime={value} title={absolute(value)}
      className={cn('tabular whitespace-nowrap', f === 'stale' ? 'text-offline' : f === 'quiet' ? 'text-attention' : 'text-ink-2', className)}>
      {compact ? shortAgo(value, at) : ago(value, at)}
    </time>
  );
}

export function CopyButton({ value, label = 'Copy', className }: { value: string; label?: string; className?: string }) {
  const [done, setDone] = useState(false);
  useEffect(() => { if (!done) return; const id = setTimeout(() => setDone(false), 1400); return () => clearTimeout(id); }, [done]);
  return (
    <button type="button" aria-label={done ? 'Copied' : label} title={label}
      onClick={() => { void navigator.clipboard?.writeText(value).then(() => setDone(true)); }}
      className={cn('inline-flex size-6 items-center justify-center rounded-md text-ink-3 hover:bg-hover hover:text-ink', className)}>
      {done ? <Check className="size-3.5 text-healthy" /> : <Copy className="size-3.5" />}
    </button>
  );
}

/** Marks assisted features: interpretation, briefs and questions. */
export function Spark({ className }: { className?: string }) {
  return <Sparkles aria-hidden className={cn('size-4 text-brand', className)} strokeWidth={2} />;
}

/** WITTCO's icon (branding/wittco-icon-small.svg: the bolder drawing, for the small sizes it is shown at). */
export function Logo({ className }: { className?: string }) {
  return (
    <svg viewBox="71 37 950 950" aria-hidden className={cn('size-7', className)}>
      <path fill="#CCCCCC" stroke="#CCCCCC" strokeWidth="24" strokeLinejoin="round" d="M 691.68 872.13 C 691.58 870.96 691.37 869.82 691.3 868.64 C 691 863.71 691.12 858.85 691.44 854.03 L 320.57 800.5 L 321.66 818.72 Z" />
      <path fill="#CCCCCC" stroke="#CCCCCC" strokeWidth="24" strokeLinejoin="round" d="M 744.59 764.21 C 749.59 761 754.9 758.23 760.4 755.81 L 530.03 253.61 L 510.88 254.76 Z" />
      <path fill="#CCCCCC" stroke="#CCCCCC" strokeWidth="24" strokeLinejoin="round" d="M 256.46 723.04 L 275.92 721.88 L 445.18 258.69 L 425.73 259.86 Z" />
      <path fill="#FFCD06" stroke="#FFCD06" strokeWidth="12" d="M 709.94 874.77 C 709.62 872.39 709.29 870.01 709.14 867.58 C 708.92 863.87 708.95 860.21 709.14 856.59 C 710.77 825.27 727.34 797.51 752.14 780.69 C 757.07 777.34 762.32 774.45 767.85 772.04 C 778.11 767.56 789.25 764.68 801.05 763.97 C 854.96 760.74 901.45 801.96 904.68 855.87 C 907.91 909.77 866.68 956.25 812.77 959.48 C 761.29 962.56 716.68 925.08 709.94 874.77 M 922.52 854.8 C 918.7 791.06 863.73 742.31 799.98 746.12 C 785.9 746.97 772.62 750.43 760.4 755.81 C 754.9 758.23 749.59 761 744.59 764.21 C 714.32 783.62 693.91 816.63 691.44 854.04 C 691.12 858.85 691 863.71 691.3 868.64 C 691.37 869.82 691.58 870.96 691.68 872.13 C 697.27 934.14 751.27 981.07 813.83 977.32 C 877.58 973.5 926.34 918.54 922.52 854.8" />
      <path fill="#F07522" d="M 321.66 818.71 L 320.57 800.5 L 315.71 719.49 L 275.92 721.88 L 256.46 723.04 L 169.03 728.27 L 177.82 874.96 L 324.5 866.17 Z" />
      <path fill="#00598D" d="M 527.62 46.47 L 408.37 53.61 L 354.93 160.45 L 420.74 260.16 L 425.73 259.86 L 445.18 258.69 L 510.88 254.76 L 530.03 253.61 L 539.99 253.01 L 593.43 146.17 Z" />
    </svg>
  );
}

export function Tooltip({ content, children, side = 'top' }: { content: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <T.Root delayDuration={350}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content side={side} sideOffset={6} collisionPadding={8}
          className="z-50 max-w-72 rounded-md bg-ink px-2.5 py-1.5 text-[12px] leading-snug text-canvas shadow-pop animate-fade">
          {content}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}

export function EmptyState({ icon, title, children, action, className }: {
  icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode; className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-14 text-center', className)}>
      {icon && <div className="mb-3 flex size-10 items-center justify-center rounded-xl border border-line bg-panel text-ink-3">{icon}</div>}
      <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
      {children && <div className="mt-1 max-w-md text-ink-2">{children}</div>}
      {action && <div className="mt-4 flex gap-2">{action}</div>}
    </div>
  );
}

export function Card({ children, className, as: As = 'section' }: { children: ReactNode; className?: string; as?: 'section' | 'div' | 'aside' }) {
  return <As className={cn('rounded-xl border border-line bg-panel shadow-card', className)}>{children}</As>;
}

export function SectionTitle({ children, aside, className }: { children: ReactNode; aside?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <h2 className="text-[13px] font-semibold tracking-[0.01em] text-ink-2">{children}</h2>
      {aside}
    </div>
  );
}
